import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MenuHero.css';

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="currentColor"
      d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24 11.36 11.36 0 0 0 3.57.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.24 1.02l-2.21 2.2z"
    />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 15h-2v-6h2zm0-8h-2V7h2z"
    />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"
    />
  </svg>
);

const CarIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <path
      fill="currentColor"
      d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-1h12v1a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-8l-2.08-5.99zM6.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM5 11l1.5-4.5h11L19 11H5z"
    />
  </svg>
);

/* Grapheme-safe initial — same helper used in MenuItemCard. Surrogate pairs
   and combining marks render as the right single glyph. */
function firstGrapheme(name) {
  if (!name) return 'R';
  const first = Array.from(name.trim())[0];
  return first ? first.toUpperCase() : 'R';
}

export default function MenuHero({
  heroStyle = 'gradient',
  heroBgImage,
  headerShrunk,
  logoUrl,
  restaurantName,
  restaurantId,
  isDemo,
  tagline,
  tableDisplay,
  isCarOrder,
  phone,
  currentLang,
  onLanguageToggle,
}) {
  const { t } = useTranslation();
  const initial = firstGrapheme(restaurantName);

  /* Normalize: anything that isn't a known style falls back to gradient.
     Three supported variants:
     - gradient: brand-color gradient surface (default for existing tenants)
     - image:    photo background with an overlay
     - minimal:  cream surface with a 1px hairline (Apple-feeling default) */
  const style = ['gradient', 'image', 'minimal'].includes(heroStyle) ? heroStyle : 'gradient';

  const tableLabelText = (() => {
    if (!tableDisplay) return '';
    if (isDemo || isCarOrder) return tableDisplay;
    return `${t('menu.hero.table', 'Table')} ${tableDisplay}`;
  })();

  return (
    <header
      className={[
        'mh-hero',
        `mh-hero--${style}`,
        headerShrunk ? 'mh-hero--compact' : '',
      ].filter(Boolean).join(' ')}
      aria-label={restaurantName || 'Restaurant'}
      style={style === 'image' && heroBgImage ? { '--mh-bg-image': `url(${heroBgImage})` } : undefined}
    >
      {/* Backgrounds are applied directly on .mh-hero--<variant> via CSS so
          contrast tooling can see them. The image variant draws its dim
          overlay via ::before — see MenuHero.css. */}
      <div className="mh-hero__inner">
        <div className="mh-hero__brand">
          {logoUrl ? (
            <div className="mh-hero__logo">
              <img src={logoUrl} alt="" />
            </div>
          ) : (
            <div className="mh-hero__logo mh-hero__logo--placeholder" aria-hidden="true">
              <span>{initial}</span>
            </div>
          )}
          <div className="mh-hero__text">
            <div className="mh-hero__title-row">
              <h1 className="mh-hero__name">{restaurantName || 'Restaurant'}</h1>
              {isDemo && (
                <span className="mh-hero__demo-pill">
                  {t('menu.hero.demoLabel', 'Demo')}
                </span>
              )}
            </div>
            {tagline && <p className="mh-hero__tagline">{tagline}</p>}
            {tableLabelText && (
              <span className="mh-hero__table-chip">
                <span className="mh-hero__table-chip-icon" aria-hidden="true">
                  {isCarOrder ? <CarIcon /> : <PinIcon />}
                </span>
                <span>{tableLabelText}</span>
              </span>
            )}
          </div>
        </div>

        <div className="mh-hero__actions">
          {phone && (
            <a
              href={`tel:${phone.replace(/\s/g, '')}`}
              className="mh-hero__action mh-hero__action--primary"
              aria-label={t('menu.hero.call', 'Call')}
              title={t('menu.hero.call', 'Call')}
            >
              <PhoneIcon />
            </a>
          )}
          <Link
            to={`/menu/${restaurantId}/info`}
            className="mh-hero__action mh-hero__action--ghost"
            onClick={(e) => e.stopPropagation()}
            aria-label={t('restaurant.info.title')}
            title={t('restaurant.info.titleShort', 'Info')}
          >
            <InfoIcon />
          </Link>
          <button
            type="button"
            className="mh-hero__action mh-hero__action--ghost mh-hero__action--lang"
            onClick={onLanguageToggle}
            aria-label={t('menu.languageToggle', 'Change language')}
            title={currentLang ? currentLang.toUpperCase() : ''}
          >
            <span className="mh-hero__lang-code">{(currentLang || '').toUpperCase()}</span>
          </button>
        </div>
      </div>
    </header>
  );
}
